/* Custom navigation menu (custom_menu)
   Loaded only when settings.custom_menu_enable is ON. Handles the horizontal
   slide between the root screen and a category screen. It NEVER fetches or
   injects content: every panel/link is already server-side rendered. */

class CustomMenu extends HTMLElement {
  constructor() {
    super();
    this._opener = null;
    this._mql = window.matchMedia('(min-width: 1150px)');
    this._onClick = this._onClick.bind(this);
    this._onBeforeOpen = this._onBeforeOpen.bind(this);
    this._onAfterOpen = this._onAfterOpen.bind(this);
    this._onBeforeClose = this._onBeforeClose.bind(this);
    this._onMqChange = this._onMqChange.bind(this);
  }

  connectedCallback() {
    this.rootScreen = this.querySelector('[data-screen="root"]');
    this.categoryScreen = this.querySelector('[data-screen="category"]');
    this.categories = this.querySelectorAll('.custom_menu__category');

    this.addEventListener('click', this._onClick);

    this.drawer = this.closest('hdt-drawer');
    if (this.drawer) {
      this.drawer.addEventListener('dialog:beforeOpen', this._onBeforeOpen);
      this.drawer.addEventListener('dialog:afterOpen', this._onAfterOpen);
      this.drawer.addEventListener('dialog:beforeClose', this._onBeforeClose);
    }
    this._mql.addEventListener('change', this._onMqChange);

    this._setState('root');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    if (this.drawer) {
      this.drawer.removeEventListener('dialog:beforeOpen', this._onBeforeOpen);
      this.drawer.removeEventListener('dialog:afterOpen', this._onAfterOpen);
      this.drawer.removeEventListener('dialog:beforeClose', this._onBeforeClose);
    }
    this._mql.removeEventListener('change', this._onMqChange);
  }

  _onClick(event) {
    /* Desktop-only: an L1 leaf with the promo carousel injected opens its panel
       instead of navigating (on mobile the <a> navigates as usual). */
    const promo = event.target.closest('[data-promo-link]');
    if (promo && this.contains(promo) && this._mql.matches) {
      event.preventDefault();
      this._openCategory(promo.getAttribute('data-category-id'), promo);
      return;
    }
    /* Desktop-only: SHIP TO / language open in-panel lists. stopPropagation keeps
       the body-delegated opener (global.js Drawer) from opening the bottom-sheets. */
    const locale = event.target.closest('[data-locale-target]');
    if (locale && this.contains(locale) && this._mql.matches) {
      event.preventDefault();
      event.stopPropagation();
      this._openCategory('custom_menu-cat-locale-' + locale.getAttribute('data-locale-target'), locale);
      return;
    }
    const opener = event.target.closest('[data-open-category]');
    if (opener && this.contains(opener)) {
      event.preventDefault();
      this._openCategory(opener.getAttribute('data-category-id'), opener);
      return;
    }
    const back = event.target.closest('[data-back]');
    if (back && this.contains(back)) {
      event.preventDefault();
      this._showRoot();
    }
  }

  _openCategory(id, opener) {
    if (!id) return;
    let active = null;
    this.categories.forEach((cat) => {
      const match = cat.getAttribute('data-category-id') === id;
      cat.hidden = !match;
      if (match) active = cat;
    });
    if (!active) return;

    if (this._mql.matches) {
      /* Desktop: no screen slide, no inert, no focus steal — both screens stay
         interactive and the panel switch is just the [hidden] toggling above. */
      this._setDesktopActive(opener || null);
      return;
    }

    this._opener = opener || null;
    if (opener) opener.setAttribute('aria-expanded', 'true');
    this._setState('category');

    const focusTarget = active.querySelector('[data-back]') || active;
    if (focusTarget && typeof focusTarget.focus === 'function') {
      focusTarget.focus({ preventScroll: true });
    }
  }

  _setDesktopActive(opener) {
    this.querySelectorAll('.custom_menu__link.is-active, .custom_menu__locale-item.is-active').forEach((el) => {
      if (el !== opener) el.classList.remove('is-active');
    });
    this.querySelectorAll('[data-open-category][aria-expanded="true"]').forEach((btn) => {
      if (btn !== opener) btn.setAttribute('aria-expanded', 'false');
    });
    if (opener) {
      opener.classList.add('is-active');
      if (opener.hasAttribute('data-open-category')) opener.setAttribute('aria-expanded', 'true');
    }
  }

  _showRoot() {
    this._setState('root');
    const opener = this._opener;
    this._opener = null;
    if (opener) {
      opener.setAttribute('aria-expanded', 'false');
      if (typeof opener.focus === 'function') opener.focus({ preventScroll: true });
    }
  }

  _setState(state) {
    this.setAttribute('data-state', state);
    if (this._mql.matches) {
      /* Desktop: both screens are always interactive (left column + content area).
         This also runs on every reconnection caused by the drawer's appendToBody
         re-parenting, so a panel activated on dialog:beforeOpen stays clickable. */
      this._toggleScreen(this.categoryScreen, true);
      this._toggleScreen(this.rootScreen, true);
      return;
    }
    const categoryActive = state === 'category';
    this._toggleScreen(this.categoryScreen, categoryActive);
    this._toggleScreen(this.rootScreen, !categoryActive);
  }

  _toggleScreen(screen, active) {
    if (!screen) return;
    if (active) {
      screen.removeAttribute('inert');
      screen.removeAttribute('aria-hidden');
    } else {
      screen.setAttribute('inert', '');
      screen.setAttribute('aria-hidden', 'true');
    }
  }

  _onBeforeOpen() {
    if (!this._mql.matches) return;
    const first = this.querySelector('.custom_menu__list [data-open-category], .custom_menu__list [data-promo-link]');
    if (first) this._openCategory(first.getAttribute('data-category-id'), first);
  }

  /* With the banners split by gender there is one slider per gender and only the
     active one is visible; reInit() on a hidden slider would measure width 0. */
  _activePromoSlider() {
    const gender = document.documentElement.dataset.customMenuGender;
    if (gender) {
      const scoped = this.querySelector('#custom_menu_promo_' + gender);
      if (scoped) return scoped;
    }
    return this.querySelector('#custom_menu_promo');
  }

  reInitPromo() {
    const slider = this._activePromoSlider();
    if (slider && typeof slider.reInit === 'function') slider.reInit();
  }

  _onAfterOpen() {
    this.reInitPromo();
  }

  _onBeforeClose() {
    this._setState('root');
    if (this._opener) {
      this._opener.setAttribute('aria-expanded', 'false');
      this._opener = null;
    }
    if (this.categories) this.categories.forEach((cat) => { cat.hidden = true; });
    this._setDesktopActive(null);
  }

  _onMqChange() {
    if (!this.drawer || !this.drawer.hasAttribute('open')) {
      this._setState('root');
      return;
    }
    this._setDesktopActive(null);
    if (this.categories) this.categories.forEach((cat) => { cat.hidden = true; });
    if (this._opener) {
      this._opener.setAttribute('aria-expanded', 'false');
      this._opener = null;
    }
    this._setState('root');
    if (this._mql.matches) {
      this._onBeforeOpen();
    } else {
      this._onAfterOpen();
    }
  }
}

if (!customElements.get('custom-menu')) {
  customElements.define('custom-menu', CustomMenu);
}

/* Home only: the drawer follows the WOMAN/MAN selector live. The flag is stamped
   by snippets/custom_menu_gender.liquid; on every other page the gender comes from
   the URL and must not change, so this listener stays inert there. The menu only
   reads the context, it never calls customGender.set(). */
document.addEventListener('custom:gender:change', (event) => {
  const root = document.documentElement;
  if (root.dataset.customMenuGenderFollow !== 'true') return;
  root.dataset.customMenuGender = event.detail.gender;
  document.querySelectorAll('custom-menu').forEach((menu) => {
    if (typeof menu.reInitPromo === 'function') menu.reInitPromo();
  });
});

/* ---------- Locale bottom-sheets (country / language) ----------
   Search filter (works inside an hdt-drawer, unlike the theme's <country-filter>
   which assumes a popover) + demo cosmetic selection with localStorage persistence.
   In real mode the rows are <button type="submit"> inside {% form 'localization' %},
   so they switch country/language natively and this script does nothing. */

function customMenuDebounce(fn, wait) {
  let t;
  return function () {
    const ctx = this;
    const args = arguments;
    clearTimeout(t);
    t = setTimeout(function () { fn.apply(ctx, args); }, wait);
  };
}

class LocaleFilter extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[name="locale_search"]');
    /* Scope to the desktop panel when inside one, else the bottom-sheet drawer
       (inside #menu_mobile the drawer scope would also hide the language rows). */
    this.sheet = this.closest('[data-locale-panel]') || this.closest('hdt-drawer');
    if (!this.input || !this.sheet) return;
    this._onInput = customMenuDebounce(this._filter.bind(this), 150);
    this.input.addEventListener('input', this._onInput);
  }
  disconnectedCallback() {
    if (this.input && this._onInput) this.input.removeEventListener('input', this._onInput);
  }
  _filter() {
    const q = (this.input.value || '').trim().toLowerCase();
    this.sheet.querySelectorAll('.custom_menu_sheet__item').forEach((item) => {
      const hay = item.getAttribute('data-search') || '';
      item.hidden = q !== '' && hay.indexOf(q) === -1;
    });
  }
}
if (!customElements.get('locale-filter')) customElements.define('locale-filter', LocaleFilter);

const CUSTOM_MENU_LOCALE_KEYS = { country: 'velez_demo_country', language: 'velez_demo_language' };

function customMenuSetFooterLabel(kind, label) {
  if (!label) return;
  document
    .querySelectorAll('[data-locale-target="' + kind + '"] [data-locale-label]')
    .forEach((el) => { el.textContent = label; });
}

function customMenuMarkSelected(sheet, row) {
  sheet.querySelectorAll('.custom_menu_sheet__row').forEach((r) => {
    const on = r === row;
    r.classList.toggle('is-selected', on);
    r.setAttribute('aria-selected', on ? 'true' : 'false');
  });
}

/* All the demo containers for a kind: the bottom-sheet drawer (mobile) and the
   desktop in-menu panels, so selections stay in sync across breakpoints. */
function customMenuLocaleScopes(kind) {
  const scopes = [];
  const sheet = document.getElementById('custom_menu_' + kind);
  if (sheet && sheet.getAttribute('data-demo') === 'true') scopes.push(sheet);
  document.querySelectorAll('[data-locale-panel="' + kind + '"][data-demo="true"]').forEach((p) => scopes.push(p));
  return scopes;
}

function customMenuMarkSelectedByValue(kind, value) {
  customMenuLocaleScopes(kind).forEach((scope) => {
    let matched = null;
    scope.querySelectorAll('.custom_menu_sheet__row').forEach((r) => {
      if (r.getAttribute('data-locale-value') === value) matched = r;
    });
    if (matched) customMenuMarkSelected(scope, matched);
  });
}

/* Re-apply a saved demo choice on load (only for demo sheets/panels). */
function customMenuApplyDemoLocale(kind) {
  if (!customMenuLocaleScopes(kind).length) return;
  let saved = null;
  try { saved = JSON.parse(localStorage.getItem(CUSTOM_MENU_LOCALE_KEYS[kind])); } catch (e) { saved = null; }
  if (!saved || !saved.value) return;
  customMenuMarkSelectedByValue(kind, saved.value);
  customMenuSetFooterLabel(kind, saved.label);
}

document.addEventListener('click', function (event) {
  const row = event.target.closest('.custom_menu_sheet__row[data-locale-value]');
  if (!row) return;
  const scope = row.closest('[data-locale-panel][data-demo="true"]') || row.closest('hdt-drawer[data-demo="true"]');
  if (!scope) return; // real mode: let the localization form submit
  event.preventDefault();
  const kind = scope.getAttribute('data-locale-panel') || (scope.id === 'custom_menu_language' ? 'language' : 'country');
  const value = row.getAttribute('data-locale-value');
  const label = row.getAttribute('data-locale-label');
  customMenuMarkSelectedByValue(kind, value);
  customMenuSetFooterLabel(kind, label);
  try {
    localStorage.setItem(CUSTOM_MENU_LOCALE_KEYS[kind], JSON.stringify({ value: value, label: label }));
  } catch (e) {}
  /* Bottom-sheet closes itself; the desktop panel is a plain div inside the open
     menu — it stays visible and the check + footer label are the feedback. */
  if (typeof scope.closeDialog === 'function') scope.closeDialog();
  else if (scope.tagName === 'HDT-DRAWER') scope.dispatchEvent(new CustomEvent('dialog:forceClose', { bubbles: true }));
});

function customMenuInitDemoLocale() {
  customMenuApplyDemoLocale('country');
  customMenuApplyDemoLocale('language');
}
if (document.readyState !== 'loading') customMenuInitDemoLocale();
else document.addEventListener('DOMContentLoaded', customMenuInitDemoLocale);
